import React, { useState, useEffect } from 'react';
import { Input, Button, Modal, Typography, Divider, Tag, Space } from 'antd';
import { SearchOutlined, FireOutlined, ClockCircleOutlined, StarOutlined } from '@ant-design/icons';

const { Search } = Input;
const { Title, Text } = Typography;

interface SearchBarProps {
  onSearch: (keyword: string) => void;
  placeholder?: string;
}

// 热门搜索关键词
const hotSearchKeywords = [
  '装饰摆件', '实用工具', '机甲模型', '可爱玩具',
  '艺术摆件', '收纳盒', '手机支架', '钥匙扣',
  '动漫手办', '桌面摆件'
];

// 推荐分类
const recommendedCategories = [
  { id: 'decorative', name: '装饰品', icon: '🎨' },
  { id: 'functional', name: '功能件', icon: '🔧' },
  { id: 'toy', name: '玩具', icon: '🎮' },
  { id: 'art', name: '艺术品', icon: '🎭' },
  { id: 'tool', name: '工具', icon: '🛠️' },
  { id: 'other', name: '其他', icon: '✨' },
];

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = '搜索3D打印作品...'
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // 从localStorage获取历史搜索
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  useEffect(() => {
    try {
      const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
      setSearchHistory(history.slice(0, 6)); // 最多显示6条历史
    } catch (error) {
      console.error('读取搜索历史失败:', error);
    }
  }, []);

  const handleSearch = (value: string) => {
    if (!value.trim()) return;

    // 保存到历史记录
    try {
      const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
      const newHistory = [value, ...history.filter((item: string) => item !== value)].slice(0, 10);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
      setSearchHistory(newHistory.slice(0, 6));
    } catch (error) {
      console.error('保存搜索历史失败:', error);
    }

    onSearch(value);
    setModalVisible(false);
  };

  const handleHotSearchClick = (keyword: string) => {
    handleSearch(keyword);
  };

  const handleCategoryClick = (category: string) => {
    handleSearch(category);
  };

  const handleClearHistory = () => {
    try {
      localStorage.setItem('searchHistory', '[]');
      setSearchHistory([]);
    } catch (error) {
      console.error('清除搜索历史失败:', error);
    }
  };

  return (
    <>
      {/* 导航栏中的搜索按钮 */}
      <Button
        type="text"
        icon={<SearchOutlined style={{ fontSize: '18px', color: '#ffffff' }} />}
        onClick={() => setModalVisible(true)}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
      />

      {/* 搜索弹窗 */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        closeIcon={<SearchOutlined style={{ fontSize: '20px' }} />}
        title={null}
        style={{ top: 80 }}
        bodyStyle={{
          padding: '24px 32px',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }}
      >
        <div>
          {/* 搜索框 */}
          <Search
            placeholder={placeholder}
            allowClear
            enterButton={
              <Button
                type="primary"
                icon={<SearchOutlined />}
                style={{
                  background: '#d32f2f',
                  borderColor: '#d32f2f',
                  borderRadius: '0 20px 20px 0',
                  height: '40px',
                  padding: '0 20px'
                }}
              >
                搜索
              </Button>
            }
            size="large"
            onSearch={handleSearch}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{
              borderRadius: '20px',
              marginBottom: '24px'
            }}
          />

          {/* 搜索历史 */}
          {searchHistory.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <Text strong style={{ fontSize: '14px', color: '#333' }}>
                  <ClockCircleOutlined style={{ marginRight: '6px', color: '#d32f2f' }} />
                  搜索历史
                </Text>
                <Button
                  type="text"
                  size="small"
                  onClick={handleClearHistory}
                  style={{ color: '#999', fontSize: '12px', padding: 0 }}
                >
                  清空
                </Button>
              </div>
              <Space wrap>
                {searchHistory.map((keyword, index) => (
                  <Tag
                    key={index}
                    onClick={() => handleSearch(keyword)}
                    closable={false}
                    style={{
                      cursor: 'pointer',
                      padding: '6px 14px',
                      fontSize: '13px',
                      background: '#f5f5f5',
                      border: 'none',
                      borderRadius: '16px',
                      color: '#666',
                      marginBottom: '8px'
                    }}
                  >
                    {keyword}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

          {/* 热门搜索 */}
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '14px', color: '#333', marginBottom: '12px', display: 'block' }}>
              <FireOutlined style={{ marginRight: '6px', color: '#ff6b6b' }} />
              热门搜索
            </Text>
            <Space wrap>
              {hotSearchKeywords.map((keyword, index) => (
                <Tag
                  key={index}
                  onClick={() => handleHotSearchClick(keyword)}
                  closable={false}
                  style={{
                    cursor: 'pointer',
                    padding: '6px 14px',
                    fontSize: '13px',
                    background: index < 3 ? '#fff1f0' : '#f5f5f5',
                    border: index < 3 ? '1px solid #ffa39e' : 'none',
                    borderRadius: '16px',
                    color: index < 3 ? '#d32f2f' : '#666',
                    marginBottom: '8px'
                  }}
                >
                  {index < 3 && <FireOutlined style={{ fontSize: '10px', marginRight: '4px' }} />}
                  {keyword}
                </Tag>
              ))}
            </Space>
          </div>

          {/* 推荐分类 */}
          <div>
            <Text strong style={{ fontSize: '14px', color: '#333', marginBottom: '12px', display: 'block' }}>
              <StarOutlined style={{ marginRight: '6px', color: '#d32f2f' }} />
              热门分类
            </Text>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: '12px'
            }}>
              {recommendedCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.name)}
                  style={{
                    textAlign: 'center',
                    padding: '16px 8px',
                    background: '#f9f9f9',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9f9f9';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>{category.icon}</div>
                  <Text style={{ fontSize: '13px', color: '#333' }}>{category.name}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SearchBar;
